import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  analyzeWorkRequestDuplicates,
  approveWorkRequest,
} from "../clients/workRequestClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { AssetId, WorkRequestId } from "../lib/workIds";
import type { RequestPriority } from "../types/workRequest";

export function useApproveWorkRequestMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      workRequestId: WorkRequestId;
      title: string;
      description: string;
      priorityCode: RequestPriority;
      confirmDuplicateWarning: boolean;
      assetId?: AssetId;
      workTypeCode: string;
      assignedUserIds: number[];
      assignedTeamIds: number[];
      basicStartDate: string | null;
      basicFinishDate: string | null;
      estimatedMinutes: number | null;
      vendorName: string | null;
    }) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      return approveWorkRequest({
        ...input,
        customerId: selectedCustomer.id,
        facilityId: selectedFacility.id,
      });
    },
    onError: (error) => {
      toast.error(
        error.message || "Unable to approve and convert the Work Request.",
        { icon: true },
      );
    },
    onSuccess: (result, variables) => {
      const savedDetails = [
        variables.assignedUserIds.length > 0 ||
        variables.assignedTeamIds.length > 0
          ? "assignment"
          : null,
        variables.basicStartDate !== null || variables.basicFinishDate !== null
          ? "schedule"
          : null,
        variables.estimatedMinutes !== null ? "estimate" : null,
        variables.vendorName !== null ? "vendor" : null,
      ].filter((detail): detail is string => detail !== null);
      const detailMessage =
        savedDetails.length > 0 ? ` ${savedDetails.join(", ")} saved.` : "";
      toast.add(
        `Work Request ${result.requestNumber} approved and converted to ${result.workOrderNumber}.${detailMessage}`,
        { icon: true },
      );
      queryClient.invalidateQueries({
        queryKey: ["work-request-approval", result.workRequestId],
      });
      queryClient.invalidateQueries({ queryKey: ["work-requests"] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
    },
  });
}

export function useWorkRequestDuplicateAnalysisMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  return useMutation({
    mutationFn: ({
      workRequestId,
      assetId,
      description,
    }: {
      workRequestId: WorkRequestId;
      assetId: number;
      description: string;
    }) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      return analyzeWorkRequestDuplicates({
        workRequestId,
        assetId,
        description,
        customerId: selectedCustomer.id,
        facilityId: selectedFacility.id,
      });
    },
  });
}
